import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Lineitem } from '../interfaces/Ordel.Interface';
import { useUpdateOrderLineItemMutation } from '../redux/api/order';
import {
  useCreateBallooningProjectMutation,
  useLazyGetBallooningProjectByLineItemQuery,
  useResetInspectionPlanMutation,
  useSetupInspectionPlanMutation,
} from '../redux/api/ballooning';

type StatusValue = 'Not Started' | 'In Progress' | 'Completed' | string;

interface HandleOptions {
  navigateOnInProgress?: boolean;
  navigateOnCompleted?: boolean;
}

interface HandleParams {
  lineItem: Lineitem;
  newStatus: StatusValue;
  prevStatus?: StatusValue | null;
  options?: HandleOptions;
}

export const useBallooningStatusChange = () => {
  const navigate = useNavigate();
  const [updateLineItemMutation] = useUpdateOrderLineItemMutation();
  const [createBallooningProject] = useCreateBallooningProjectMutation();
  const [triggerGetProjectByLineItem] = useLazyGetBallooningProjectByLineItemQuery();
  const [setupInspectionPlan] = useSetupInspectionPlanMutation();
  const [resetInspectionPlan] = useResetInspectionPlanMutation();

  const handleStatusChange = async ({ lineItem, newStatus, prevStatus, options }: HandleParams) => {
    const navigateOnInProgress = options?.navigateOnInProgress ?? true;
    const navigateOnCompleted = options?.navigateOnCompleted ?? false;

    try {
      // Prevent changing to In Progress when there is no drawing to balloon
      if (
        newStatus === 'In Progress' &&
        !lineItem.ballooning_project_id &&
        !lineItem.drawing_file
      ) {
        message.warning('No PDF file associated with this item.');
        return;
      }

      if (newStatus === 'Completed') {
        let projectId = lineItem.ballooning_project_id as string | undefined;
        let project: any | undefined = undefined;
        if (!projectId) {
          try {
            project = await triggerGetProjectByLineItem({ id: lineItem.id }).unwrap();
            projectId = project?.id;
          } catch {}
        } else {
          try {
            project = await triggerGetProjectByLineItem({ id: lineItem.id }).unwrap();
          } catch {}
        }

        if (!projectId) {
          message.error('Cannot complete ballooning without a project. Set to In Progress first.');
          return;
        }

        if (project?.is_locked) {
          message.error('Project is locked because some samples for this product have been measured already.');
          return;
        }
      } else {
        if (lineItem.ballooning_project_id) {
          try {
            const project = await triggerGetProjectByLineItem({ id: lineItem.id }).unwrap();
            if (project?.is_locked) {
              message.error(
                'Project is locked because some samples for this product have been measured already.'
              );
              return;
            }
          } catch (e) {
            // If the project cannot be fetched, proceed without the lock check
          }
        }
      }

      if (!updateLineItemMutation) {
         throw new Error('updateLineItemMutation is not defined');
      }

      await updateLineItemMutation({ id: lineItem.id, values: { ballooning_status: newStatus } }).unwrap();

      if (newStatus === 'In Progress') {
        let projectId = lineItem.ballooning_project_id as string | undefined;

        if (!projectId) {
          const creatingKey = 'creating-project';
          message.loading({ content: `Creating project for ${lineItem.name || 'this item'}...`, key: creatingKey, duration: 0 });
          try {
            const response = await createBallooningProject({ body: { order_line_item: lineItem.id } }).unwrap();
            projectId = response.id;
            message.success({ content: 'Project created', key: creatingKey });
          } catch (err) {
            message.error({ content: 'Failed to create project', key: creatingKey });
            throw err;
          }
        }

        if (navigateOnInProgress && projectId) {
          navigate(`/ballooning/${projectId}`);
        }
      }

      if (newStatus === 'Completed') {
        let projectId = lineItem.ballooning_project_id as string | undefined;
        if (!projectId) {
          try {
            const project = await triggerGetProjectByLineItem({ id: lineItem.id }).unwrap();
            projectId = project?.id;
          } catch {}
        }

        if (projectId) {
          try {
            await setupInspectionPlan({ id: projectId }).unwrap();
          } catch (e) {
            // Setup failed — revert ballooning status so the user can retry
            message.error('Failed to setup inspection plan. Reverting status.');
            await updateLineItemMutation({ id: lineItem.id, values: { ballooning_status: 'In Progress' } }).unwrap();
            return;
          }
        }

        if (navigateOnCompleted) {
          navigate('/production/queues');
        }
      } else if (prevStatus === 'Completed') {
        let projectId = lineItem.ballooning_project_id as string | undefined;
        if (!projectId) {
          try {
            const project = await triggerGetProjectByLineItem({ id: lineItem.id }).unwrap();
            projectId = project?.id;
          } catch (e) {
            // ignore
          }
        }

        if (projectId) {
          try {
            await resetInspectionPlan({ id: projectId }).unwrap();
          } catch (e) {
            message.error('Failed to reset inspection plan');
          }
        }
      }

      message.success(`Ballooning status updated to ${newStatus} successfully!`);
    } catch (error: any) {
      console.error('Error updating ballooning status:', error);
      const errMsg = error?.data?.detail || error?.message || 'Unknown error';
      message.error(`Failed to update ballooning status: ${errMsg}`);
    }
  };

  return { handleBallooningStatusChange: handleStatusChange };
};
